/* ==========================================================================
   ACDC Shoppable Video Reels — Standalone JS (Infinite Carousel)
   Ticket: TLSB-730

   Layout: 5 visible slots — peek | side | CENTER | side | peek
   The carousel is infinite (wraps around).
   Active reel is always the center slot.
   ========================================================================== */

if (!window.customElements.get('acdc-shoppable-video-reels')) {

  class AcdcShoppableVideoReels extends HTMLElement {

    constructor() {
      super();
      this._activeIndex = 0;
      this._totalReels = 0;
      this._isIntersecting = false;
      this._isAnimating = false;
      this._globalMuted = true;
      this._touchStartX = 0;
      this._touchDeltaX = 0;
    }

    connectedCallback() {
      this._carousel = this.querySelector('[data-svr-carousel]');
      this._reels = Array.from(this.querySelectorAll('[data-svr-reel]'));
      this._prevBtn = this.querySelector('[data-svr-prev]');
      this._nextBtn = this.querySelector('[data-svr-next]');
      this._dots = Array.from(this.querySelectorAll('[data-svr-dot]'));
      this._productCards = Array.from(this.querySelectorAll('[data-svr-product-card]'));
      this._totalReels = this._reels.length;

      if (!this._carousel || this._totalReels === 0) return;

      this._bindEvents();
      this._setupIntersectionObserver();

      // Start with index 0 as active
      this._activeIndex = 0;
      this._render(false);
    }

    disconnectedCallback() {
      if (this._sectionObserver) this._sectionObserver.disconnect();
      this._pauseAllVideos();
    }

    // ---- Helpers for infinite index wrapping ----

    _wrapIndex(i) {
      return ((i % this._totalReels) + this._totalReels) % this._totalReels;
    }

    // Returns the shortest signed offset from `from` to `to` on a ring of size n
    _ringOffset(from, to, n) {
      const raw = ((to - from) % n + n) % n;
      return raw > n / 2 ? raw - n : raw;
    }

    // ---- Event binding ----

    _bindEvents() {
      if (this._prevBtn) {
        this._prevBtn.addEventListener('click', () => this._navigate(-1));
      }
      if (this._nextBtn) {
        this._nextBtn.addEventListener('click', () => this._navigate(1));
      }

      // Dots navigate directly to the target index
      this._dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          if (i === this._activeIndex || this._isAnimating) return;
          this._goTo(i);
        });
      });

      // Touch/swipe on the carousel area
      this._carousel.addEventListener('touchstart', (e) => {
        this._touchStartX = e.touches[0].clientX;
        this._touchDeltaX = 0;
      }, { passive: true });

      this._carousel.addEventListener('touchmove', (e) => {
        this._touchDeltaX = e.touches[0].clientX - this._touchStartX;
      }, { passive: true });

      this._carousel.addEventListener('touchend', () => {
        if (Math.abs(this._touchDeltaX) > 40) {
          this._navigate(this._touchDeltaX < 0 ? 1 : -1);
        }
      });

      // Play/pause and mute buttons (delegated)
      this.addEventListener('click', (e) => {
        const playBtn = e.target.closest('[data-svr-play]');
        const muteBtn = e.target.closest('[data-svr-mute]');

        if (playBtn) {
          e.stopPropagation();
          this._togglePlayPause(playBtn.closest('[data-svr-reel]'));
          return;
        }

        if (muteBtn) {
          e.stopPropagation();
          this._toggleMute();
          return;
        }

        // Quick-add
        const addBtn = e.target.closest('[data-svr-quick-add]');
        if (addBtn) {
          e.preventDefault();
          e.stopPropagation();
          this._quickAdd(addBtn);
          return;
        }
      });

      // Click on a reel: non-active → jump to it; active with product URL → navigate
      this._reels.forEach((reel) => {
        reel.addEventListener('click', (e) => {
          if (e.target.closest('button, a, [data-svr-quick-add]')) return;
          const reelIdx = parseInt(reel.dataset.index, 10);
          if (reelIdx !== this._activeIndex) {
            this._goTo(reelIdx);
            return;
          }
          // Active reel: navigate to product URL if configured
          const productUrl = reel.dataset.productUrl;
          if (productUrl) {
            window.location.href = productUrl;
          }
        });
      });

      // Theme editor events
      document.addEventListener('shopify:section:load', (e) => {
        if (e.target.contains(this)) this.connectedCallback();
      });
      document.addEventListener('shopify:section:unload', (e) => {
        if (e.target.contains(this)) this._pauseAllVideos();
      });
    }

    // ---- Intersection Observer ----

    _setupIntersectionObserver() {
      this._sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this._isIntersecting = entry.isIntersecting;
          if (entry.isIntersecting) {
            this._playActiveVideo();
          } else {
            this._pauseAllVideos();
          }
        });
      }, { threshold: 0.3 });
      this._sectionObserver.observe(this);
    }

    // ---- Navigation ----

    _navigate(direction) {
      if (this._isAnimating || this._totalReels <= 1) return;
      this._isAnimating = true;

      const prevIndex = this._activeIndex;
      this._activeIndex = this._wrapIndex(this._activeIndex + direction);

      this._pauseVideo(this._reels[prevIndex]);
      this._render(true);

      // Unlock after transition completes
      setTimeout(() => {
        this._isAnimating = false;
      }, 450);
    }

    _goTo(index) {
      if (index === this._activeIndex || this._isAnimating || this._totalReels <= 1) return;
      this._isAnimating = true;

      const prevIndex = this._activeIndex;
      this._activeIndex = this._wrapIndex(index);

      this._pauseVideo(this._reels[prevIndex]);
      this._render(true);

      setTimeout(() => {
        this._isAnimating = false;
      }, 450);
    }

    // ---- Render: position all reels based on their offset from active ----

    _render(animate) {
      const n = this._totalReels;
      const active = this._activeIndex;

      this._reels.forEach((reel, i) => {
        const offset = this._ringOffset(active, i, n);

        // Slots: -2 (left peek), -1 (left side), 0 (center), +1 (right side), +2 (right peek)
        // Anything beyond ±2 is hidden off-screen
        reel.dataset.slot = offset;
        reel.classList.toggle('is-active', offset === 0);

        if (animate) {
          reel.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1), opacity 0.4s ease, height 0.4s ease';
        } else {
          reel.style.transition = 'none';
        }

        // translateY(-50%) keeps every reel vertically centered regardless of height
        if (Math.abs(offset) <= 3) {
          reel.style.display = '';
          reel.style.opacity = Math.abs(offset) <= 2 ? '1' : '0';
          reel.style.transform = `translateX(calc(${offset} * (var(--svr-reel-width) + var(--svr-gap)))) translateY(-50%)`;
          reel.style.zIndex = 10 - Math.abs(offset);
        } else {
          reel.style.display = 'none';
          reel.style.transform = 'translateY(-50%)';
        }
      });

      // Update dots
      this._dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === active);
      });

      // Update product cards
      this._productCards.forEach(card => {
        card.hidden = parseInt(card.dataset.reelIndex, 10) !== active;
      });

      // Arrows never disabled — infinite carousel
      if (this._prevBtn) this._prevBtn.disabled = false;
      if (this._nextBtn) this._nextBtn.disabled = false;

      // Play active video
      if (this._isIntersecting) {
        this._playActiveVideo();
      }
    }

    // ---- Video playback ----

    _getVideo(reel) {
      return reel ? reel.querySelector('video') : null;
    }

    _playActiveVideo() {
      const reel = this._reels[this._activeIndex];
      if (!reel) return;
      const video = this._getVideo(reel);
      if (!video) return;

      video.muted = this._globalMuted;
      const poster = reel.querySelector('[data-svr-poster]');
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          if (poster) poster.classList.add('is-hidden');
          this._updatePlayIcon(reel, true);
        }).catch(() => {
          this._updatePlayIcon(reel, false);
        });
      }
    }

    _pauseVideo(reel) {
      const video = this._getVideo(reel);
      if (!video) return;
      video.pause();
      this._updatePlayIcon(reel, false);
    }

    _pauseAllVideos() {
      this._reels.forEach(reel => this._pauseVideo(reel));
    }

    _togglePlayPause(reel) {
      const video = this._getVideo(reel);
      if (!video) return;

      if (video.paused) {
        video.muted = this._globalMuted;
        video.play().then(() => {
          this._updatePlayIcon(reel, true);
          const poster = reel.querySelector('[data-svr-poster]');
          if (poster) poster.classList.add('is-hidden');
        }).catch(() => {});
      } else {
        video.pause();
        this._updatePlayIcon(reel, false);
      }
    }

    _toggleMute() {
      this._globalMuted = !this._globalMuted;
      this._reels.forEach(reel => {
        const video = this._getVideo(reel);
        if (video) video.muted = this._globalMuted;
      });
      this.querySelectorAll('[data-svr-mute]').forEach(btn => {
        const muteIcon = btn.querySelector('[data-icon-muted]');
        const unmuteIcon = btn.querySelector('[data-icon-unmuted]');
        if (muteIcon) muteIcon.hidden = !this._globalMuted;
        if (unmuteIcon) unmuteIcon.hidden = this._globalMuted;
      });
    }

    _updatePlayIcon(reel, isPlaying) {
      const playBtn = reel.querySelector('[data-svr-play]');
      if (!playBtn) return;
      const playIcon = playBtn.querySelector('[data-icon-play]');
      const pauseIcon = playBtn.querySelector('[data-icon-pause]');
      if (playIcon) playIcon.hidden = isPlaying;
      if (pauseIcon) pauseIcon.hidden = !isPlaying;
    }

    // ---- Quick add to cart ----

    async _quickAdd(btn) {
      const variantId = btn.dataset.variantId;
      if (!variantId || btn.classList.contains('is-loading')) return;

      btn.classList.add('is-loading');

      try {
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        }));

        const formData = new FormData();
        formData.set('id', variantId);
        formData.set('quantity', '1');
        if (sectionsToBundle.length > 0) {
          formData.set('sections', sectionsToBundle.join(','));
          formData.set('sections_url', window.location.pathname);
        }

        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

        const responseJson = await response.json();

        if (response.ok) {
          const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
          const cartContent = await cartResponse.json();
          cartContent['sections'] = responseJson['sections'];

          const items = responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson];

          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart: cartContent }
          }));

          if (window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.addedToCart) {
            document.dispatchEvent(new CustomEvent('toast:show', {
              detail: { message: window.themeVariables.strings.addedToCart.replace('{{product_title}}', items?.[0]?.title || '') }
            }));
          }
        } else {
          document.dispatchEvent(new CustomEvent('toast:show', {
            detail: { message: responseJson['description'] || responseJson['message'] || 'Error adding to cart', tone: 'error' }
          }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      } catch (err) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('toast:show', {
          detail: { message: 'Error adding to cart', tone: 'error' }
        }));
      } finally {
        btn.classList.remove('is-loading');
      }
    }
  }

  window.customElements.define('acdc-shoppable-video-reels', AcdcShoppableVideoReels);
}